import { Box, Fab, Grid, IconButton, Paper, TextField, Typography, Button } from "@mui/material";
import { Search } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";

import { Field, Form, Formik } from "formik";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorComponent } from "../../../../components/error/error.component";
import Loading from "../../../../components/loading/loading";
import TablePaginationComponent from "../../../../components/table/table-pagination.component";
import { useDocumentTitle } from "../../../../contexts/document-title-context";
import { PaginatedResult } from "../../../../models/default/paginated-result";
import { Bom } from "../../../../models/general/bom";
import { BomComponent } from "../../../../models/general/bom-component";
import { URLS } from "../../../../services/app-urls";
import { BaseService } from "../../../../services/base-service";
import { translate } from "../../../../translate/translate";
import { parseLocaleNumber } from "../../../../utils/locale-number-utils";
import BomFilterMenuComponent from "./components/filter-menu.component";
import BomTypeCell from "../../../../components/table/bom-type-cell.component";
import { ReactComponent as PerfeitoIcon } from "../../../../../assets/images/ic-perfeitos.svg";
import { ReactComponent as ImperfeitoIcon } from "../../../../../assets/images/ic-imperfeitos.svg";
import { ReactComponent as PhantonIcon } from "../../../../../assets/images/ic-phanton.svg";

const BOMComponentComponent = () => {
  const navigate = useNavigate();
  const { action: bomId } = useParams();
  const { setDocumentTitle } = useDocumentTitle();

  const service = new BaseService<Bom>(URLS.BOM);

  const [bom, setBom] = useState<Bom | null>(null);
  const [dataSource, setDataSource] = useState<BomComponent[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchBOM, setSearchBOM] = useState("");
  const [searchItem, setSearchItem] = useState("");
  const [searchLevel, setsearchLevel] = useState("");
  const [searchGetChildren, setSearchGetChildren] = useState(false);
  const [searchFilterBy, setSearchFilterBy] = useState("all");
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Filter menu state
  const [anchorElFilter, setAnchorElFilter] = useState<null | HTMLElement>(null);
  const openFilter = Boolean(anchorElFilter);

  const handleClickFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElFilter(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setAnchorElFilter(null);
  };

  const handleApplyFilters = (filterType: string, showChildren: boolean) => {
    setSearchFilterBy(filterType);
    setSearchGetChildren(showChildren);
    if (page === 0) {
      search({ filterBy: filterType, children: showChildren });
    } else {
      setPage(0);
    }
  };

  const childMatchesSearch = (child: any) => {
    return child.component?.description.toLowerCase().includes(searchBOM.toLowerCase());
  };

  const search = async (filterOverrides?: { filterBy?: string; children?: boolean }) => {
    if (!bomId) return;

    setIsLoading(true);

    const useFilterBy = filterOverrides?.filterBy ?? searchFilterBy;
    const useChildren = filterOverrides?.children ?? searchGetChildren;

    service.clearParameters();
    if (searchBOM) service.addParameter("search", searchBOM);
    if (searchItem) {
      const sanitizedItem = searchItem.replace(/^,+|,+$/g, "").replace(/,{2,}/g, ",");
      if (sanitizedItem) service.addParameter("item", sanitizedItem);
    }
    if (searchLevel) service.addParameter("level", searchLevel);
    if (useChildren) service.addParameter("get_children", useChildren);
    service.addParameter("filter_by", useFilterBy);
    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    if (bom === null) {
      service.getById(bomId).then(({ data }) => {
        setBom(data);
        setDocumentTitle(data?.bom_number);
      });
    }

    const getComponents = service.getFromDetailRoutePaginate(bomId, "components")
      .then((response) => {
        setPagination(response.data);
        setDataSource(response.data.results);

        if (!searchBOM) {
          setOpenRows({});
          return;
        }

        const openRowsCopy = { ...openRows };

        const openMatchingItems = (items: any[]) => {
          items.forEach((item: any) => {
            openRowsCopy[item.id] = true;

            if (item.children_components?.length > 0) {
              item.children_components.forEach((child: any) => {
                if (childMatchesSearch(child)) {
                  openRowsCopy[child.id] = true;
                }
              });

              openMatchingItems(item.children_components);
            }
          });
        };

        openMatchingItems(response.data.results);
        setOpenRows(openRowsCopy);
      });

    Promise.all([getComponents])
      .catch(ErrorComponent)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    search();
  }, [page, rowsPerPage]);

  const mappedDataSource = useMemo(() => {
    return dataSource.map((bom_component) => ({
      ...bom_component,
      description: bom_component.component?.description || "N/A",
      part_number: bom_component.component?.part_number || "N/A",
    }));
  }, [dataSource]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderTypeCell = (row: any) => (
    <BomTypeCell is_phantom={row.is_phantom} is_imperfect={row.is_imperfect} />
  );

  const renderQuantityCell = (quantity: number | string) => (
    <Box display="flex" justifyContent="flex-end">
      {parseLocaleNumber(Number(quantity))}
    </Box>
  );

  const renderUnitMeasureCell = (unitMeasure: string) => (
    <Box display="flex" justifyContent="flex-end">
      {unitMeasure}
    </Box>
  );

  const renderUsagePriorityCell = (consumption_priority: number | string) => {
    const value = Number(consumption_priority) === 0 ? null : Number(consumption_priority);
    return (
      <Box display="flex" justifyContent="flex-end">
        {value}
      </Box>
    );
  };

  const renderPurchasePriorityCell = (usagePriority: number | string) => (
    <Box display="flex" justifyContent="flex-end">
      {usagePriority === 1 ? translate.t("prime") : translate.t("alternative")}
    </Box>
  );

  const renderItemCell = (item: string) => (
    <Box display="flex" justifyContent="flex-end">
      {item}
    </Box>
  );

  const renderPartNumberCell = (partNumber: string, row: any) => (
    <Box display="flex" justifyContent="flex-start">
      <Typography fontWeight={row.level === 1 ? "bold" : "normal"}>
        {partNumber}
      </Typography>
    </Box>
  );

  const renderCellHandler = (column: string, index: number, row: any) => {
    if (column === "type") return renderTypeCell(row);
    if (column === "quantity") return renderQuantityCell(row["quantity"]);
    if (column === "unit_measure") return renderUnitMeasureCell(row["unit_measure"]);
    if (column === "purchase_priority") return renderPurchasePriorityCell(row["purchase_priority"]);
    if (column === "consumption_priority") return renderUsagePriorityCell(row["consumption_priority"]);
    if (column === "item") return renderItemCell(row["item"]);
    if (column === "level") return renderItemCell(row["level"]);
    if (column === "part_number") return renderPartNumberCell(row["part_number"], row);

    return null;
  };

  const headerCellCustomStyleHandler = (column: any) => {
    if (["use_quantity", "unit_measure", "purchase_priority", "usage_priority", "level", "item"].includes(column)) {
      return { textAlign: "end" };
    } else if (["type"].includes(column)) {
      return { textAlign: "center" };
    }
    return null;
  };

  const getRowStyle = (row: any) => {
    const baseStyle = {
      backgroundColor: row.level === 1 ? "#ffffff !important" : "#f5f5f5 !important",
      "&:hover": {
        backgroundColor: "#e3f2fd !important",
      },
    };
    return baseStyle;
  };

  return (
    <section>
      <Loading open={isLoading} />
      <div className="floating-title floating-title-item" style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon color="primary" fontSize="large" />
        </IconButton>
        <Typography variant="h5">{bom?.bom_number}</Typography>
      </div>

      <Grid item xs={12}>
        <Paper variant="outlined">
          <Box p={2}>
            <Formik
              enableReinitialize
              initialValues={{ bomNumber: "" }}
              onSubmit={(values, { setSubmitting }) => {
                if (page === 0) {
                  search();
                } else {
                  setPage(0);
                }
                setSubmitting(false);
              }}
            >
              {() => (
                <Form autoComplete="off">
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={2}>
                      <Field
                        as={TextField}
                        name="level"
                        type="number"
                        label={`${translate.t("level")}`}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={searchLevel}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => setsearchLevel(event.target.value)}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Field
                        as={TextField}
                        name="item"
                        type="text"
                        label={`${translate.t("item")}`}
                        placeholder="Ex: 123,124"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={searchItem}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          const value = event.target.value.replace(/[^0-9,]/g, "");
                          setSearchItem(value);
                        }}
                      />
                    </Grid>
                    <Grid item xs>
                      <Field
                        as={TextField}
                        name="part_number"
                        label={`${translate.t("part_number")}, ${translate.t("description")}`}
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={searchBOM}
                        onChange={(event: ChangeEvent<{ value: unknown }>) => setSearchBOM(event.target.value as string)}
                      />
                    </Grid>
                    <Grid item>
                      <Fab size="small" type="submit" color="primary">
                        <Search />
                      </Fab>
                    </Grid>
                    <Grid item>
                      <Button
                        startIcon={<FilterListOutlinedIcon />}
                        variant="outlined"
                        color="primary"
                        onClick={handleClickFilter}
                        sx={{ height: "40px" }}
                      >
                        {translate.t("filter")}
                      </Button>
                      <BomFilterMenuComponent
                        anchorEl={anchorElFilter}
                        open={openFilter}
                        onClose={handleCloseFilter}
                        onApplyFilters={handleApplyFilters}
                        selectedFilterType={searchFilterBy}
                        selectedShowChildren={searchGetChildren}
                      />
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </Box>
        </Paper>
      </Grid>

      <Paper variant="outlined" sx={{ marginTop: "16px" }}>
        <TablePaginationComponent
          legendComponent={
            <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap", width: "max-content", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", marginRight: "24px" }}>
                <PerfeitoIcon style={{ marginRight: '6px', width: 16, height: 16, flexShrink: 0 }} />
                <Typography variant="body2" color="textSecondary" style={{ margin: 0 }}>{translate.t("perfect")}</Typography>
              </div>
              <div style={{ display: "flex", alignItems: "center", marginRight: "24px" }}>
                <ImperfeitoIcon style={{ marginRight: '6px', width: 16, height: 16, flexShrink: 0 }} />
                <Typography variant="body2" color="textSecondary" style={{ margin: 0 }}>{translate.t("is_imperfect")}</Typography>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <PhantonIcon style={{ marginRight: '6px', width: 16, height: 16, flexShrink: 0 }} />
                <Typography variant="body2" color="textSecondary" style={{ margin: 0 }}>{translate.t("phantom")}</Typography>
              </div>
            </div>
          }
          state={mappedDataSource}
          displayedColumnsShow={[
            "level",
            "item",
            "part_number",
            "description",
            "type",
            "use_quantity",
            "purchase_priority",
            "usage_priority",
            "unit_measure",
          ]}
          displayedColumns={[
            "level",
            "item",
            "part_number",
            "description",
            "type",
            "quantity",
            "purchase_priority",
            "consumption_priority",
            "unit_measure",
          ]}
          count={pagination?.count}
          next={pagination?.next}
          previous={pagination?.previous}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          columnStyles={{
            1: "5%",
            2: "5%",
            3: "15%",
            4: "22%",
            5: "5%",
            6: "9%",
            7: "15%",
            8: "13%",
            9: "11%",
          }}
          headerCellCustomStyleHandler={headerCellCustomStyleHandler}
          renderCellHandler={{
            type: renderCellHandler,
            quantity: renderCellHandler,
            unit_measure: renderCellHandler,
            consumption_priority: renderCellHandler,
            purchase_priority: renderCellHandler,
            item: renderCellHandler,
            level: renderCellHandler,
            part_number: renderCellHandler,
          }}
          rowCustomStyleHandler={getRowStyle}
        />
      </Paper>
    </section>
  );
};

export default BOMComponentComponent;
